// ============================================================================
// Reference ghost curves for the trajectory view (GDD §15.1-3): the balanced
// reference and the disciplined grinder, engine-computed once and memoized.
//
// The strategy CONSTANTS here mirror sim/strategies.ts TUNING (the harness is
// not an app dependency). Drift guard: apps/web/test/skills.test.ts asserts
// the ghost-balanced estate matches the harness's pinned reference value, so
// a TUNING change that isn't mirrored here fails CI instead of quietly
// showing players a stale ghost.
// ============================================================================

import { PARAMS, simulateLife } from '@lifespan/engine';
import type { Params, State, Strategy } from '@lifespan/engine';

/** Mirrors sim/strategies.ts TUNING (v1.3.0). */
export const GHOST_TUNING = {
  balancedLiving: 0.2,
  balancedLateLiving: 0.065,
  balancedSabLiving: 0.35,
  grinderLiving: 0.171,
} as const;

const sabDone = (st: State, P: Params) =>
  st.sabYearsSinceSkill >= P.sabMinYears - 1e-9;

/** The GDD's intended play (two-phase, v1.3.0) — sim/strategies.ts `balanced`. */
export const ghostBalanced: Strategy = (st, P) => {
  if (st.K >= 6)
    return { mode: 'std', livingFrac: GHOST_TUNING.balancedLateLiving, study: false };
  const next = st.K + 1;
  if (P.sabRequiredForSkills.includes(next) && !sabDone(st, P)) {
    return { mode: 'sab', livingFrac: GHOST_TUNING.balancedSabLiving, study: false };
  }
  return { mode: 'std', livingFrac: GHOST_TUNING.balancedLiving, study: true };
};

/** The disciplined grinder — sim/strategies.ts `pureGrinder`. */
export const ghostGrinder: Strategy = () => ({
  mode: 'ot',
  livingFrac: GHOST_TUNING.grinderLiving,
  study: false,
});

export interface GhostCurves {
  balanced: { age: number; S: number }[];
  grinder: { age: number; S: number }[];
  balancedEstate: number;
  grinderEstate: number;
}

// memoized per Params identity — a caller passing a different ParamSet must
// never receive curves computed for an earlier one (the app has a single
// PARAMS constant and persist.ts rejects other paramSetVersions, but this
// function must be honest about its own signature, not lean on that)
let cached: { P: Params; curves: GhostCurves } | null = null;

/** Yearly net-worth curves for both references (computed once per ParamSet). */
export function ghostCurves(P: Params = PARAMS): GhostCurves {
  if (cached && cached.P === P) return cached.curves;
  const b = simulateLife(ghostBalanced, P, {});
  const g = simulateLife(ghostGrinder, P, {});
  cached = {
    P,
    curves: {
      balanced: b.yearly.map((y) => ({ age: y.age, S: y.S })),
      grinder: g.yearly.map((y) => ({ age: y.age, S: y.S })),
      balancedEstate: b.estate,
      grinderEstate: g.estate,
    },
  };
  return cached.curves;
}
